import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { Input } from "@/components/ui/input";
import { INSTRUMENTS } from "@/lib/instruments";

/**
 * Autocomplete input for comma-separated instruments.
 * Drop-in replacement for shadcn Input — accepts the same props.
 */
export function InstrumentInput({ value, onChange, className, ...props }) {
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const containerRef = useRef(null);

  // Extract the current segment (text after last comma)
  const segments = useMemo(() => value ? value.split(",") : [""], [value]);
  const currentSegment = segments[segments.length - 1].trimStart();

  // Filter suggestions
  const suggestions = currentSegment.length > 0
    ? INSTRUMENTS.filter((inst) => {
        const lower = inst.toLowerCase();
        const query = currentSegment.toLowerCase();
        // Exclude instruments already entered
        const existing = segments.slice(0, -1).map((s) => s.trim().toLowerCase());
        return lower.startsWith(query) && !existing.includes(lower);
      }).slice(0, 5)
    : [];

  const isOpen = showDropdown && suggestions.length > 0;

  // Reset highlight when suggestions change
  useEffect(() => {
    setHighlightIndex(0);
  }, [suggestions.length, currentSegment]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const selectSuggestion = useCallback((instrument) => {
    const prefix = segments.slice(0, -1).join(",");
    const newValue = prefix
      ? `${prefix}, ${instrument}, `
      : `${instrument}, `;
    onChange({ target: { value: newValue } });
    setShowDropdown(false);
  }, [segments, onChange]);

  const handleKeyDown = (e) => {
    if (!isOpen) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIndex((i) => (i + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIndex((i) => (i - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      selectSuggestion(suggestions[highlightIndex]);
    } else if (e.key === "Escape") {
      setShowDropdown(false);
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <Input
        value={value}
        onChange={(e) => {
          onChange(e);
          setShowDropdown(true);
        }}
        onFocus={() => setShowDropdown(true)}
        onKeyDown={handleKeyDown}
        className={className}
        {...props}
      />
      {isOpen && (
        <div className="absolute z-50 left-0 right-0 top-full mt-1 bg-[var(--studio-overlay)] border border-[var(--studio-divider-strong)] rounded-lg overflow-hidden shadow-[var(--studio-shadow)]">
          {suggestions.map((inst, i) => (
            <button
              key={inst}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                selectSuggestion(inst);
              }}
              className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                i === highlightIndex
                  ? "bg-[var(--studio-tint-strong)] text-[var(--text-primary)]"
                  : "text-[var(--text-secondary)] hover:bg-[var(--studio-tint)]"
              }`}
            >
              {inst}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
